import { Vector3, Web3DViewer } from '@technology/web3d';
import {
  CloudMarkupTool,
  FreelineMarkupTool,
  LineMarkupTool,
  ArrowMarkupTool,
  TextMarkupTool,
  CloudMarkup as Web3DCloudMarkup,
  FreelineMarkup as Web3DFreelineMarkup,
  LineMarkup as Web3DLineMarkup,
  ArrowMarkup as Web3DArrowMarkup,
  TextMarkup as Web3DTextMarkup,
  Markup as ViewerMarkup,
} from '@technology/web3d-plugin-markup';
import { UnreachableCaseError } from 'ts-essentials';
import {
  rgbToWeb3dColor,
  RGB_MAX_VALUE,
  ensureAlpha,
  convertMillimeterDiameterToMeterRadius,
  convertMeterRadiusToMillimeterDiameter,
  convertMillimetersToMeters,
  convertMetersToMillimeters,
  web3dColorToRgb,
} from '@trimble/utilities';
import { convertConnectDirectionToViewerDirection } from '../views/camera-conversion';
import { stampCanonicalId } from '../base/viewer-annotation-identity';
import {
  CloudMarkup,
  RedlinesMarkup,
  LineMarkup,
  ArrowMarkup,
  TextMarkup,
  Markup,
} from './markup';
import {
  Markup as ConnectMarkup,
  MarkUpCloudType,
  MarkUpTextType,
  MarkUpLineType,
  MarkUpArrowType,
  MarkUpRedLineType,
} from '@data-access/connect/tc-tcps';

// Constants
const DEFAULT_MARKUP_COLOR = { r: 255, g: 0, b: 255 };

const REDLINES_SEGMENT_JOIN_EPSILON = 1e-9;

type FreelineMarkupToolInternal = FreelineMarkupTool & {
  _createMarkup(origin?: unknown): Web3DFreelineMarkup;
};

/**
 * Rebuild a freeline in the same layout as interactive drawing: anchor
 * {@link Web3DFreelineMarkup.position} plus local {@link Web3DFreelineMarkup.points}.
 * Position must be set before `from()` so bounding boxes and planar frustum
 * culling match the drawn geometry.
 */
function createFreelineMarkupFromWorldPoints(
  viewer: Web3DViewer,
  worldPoints: Vector3[],
  color: Web3DFreelineMarkup['color'] | undefined,
  origin?: unknown
): Web3DFreelineMarkup | null {
  if (worldPoints.length === 0) {
    return null;
  }

  const tool = viewer.tools.get(
    FreelineMarkupTool
  ) as FreelineMarkupToolInternal | null;
  if (!tool) {
    return null;
  }

  const anchor = worldPoints[0].clone();
  const localPoints = worldPoints.map((point) => point.clone().sub(anchor));
  const freelineMarkup = tool._createMarkup(origin);
  if (color !== undefined) {
    freelineMarkup.color = color;
  }
  freelineMarkup.position.copy(anchor);
  freelineMarkup.from(localPoints);
  return freelineMarkup;
}

/**
 * Append one redline segment to a polyline vertex list. Segments from
 * {@link convertViewerMarkupToMarkup} share interior vertices with the next segment;
 * those must not be duplicated when rebuilding the viewer polyline.
 */
function appendRedlineSegmentToPolyline(
  points: Vector3[],
  segmentStart: Vector3,
  segmentEnd: Vector3
): void {
  if (points.length === 0) {
    points.push(segmentStart, segmentEnd);
    return;
  }
  const last = points[points.length - 1];
  if (
    Math.abs(last.x - segmentStart.x) < REDLINES_SEGMENT_JOIN_EPSILON &&
    Math.abs(last.y - segmentStart.y) < REDLINES_SEGMENT_JOIN_EPSILON &&
    Math.abs(last.z - segmentStart.z) < REDLINES_SEGMENT_JOIN_EPSILON
  ) {
    points.push(segmentEnd);
  } else {
    points.push(segmentStart, segmentEnd);
  }
}

type ConnectCloudMarkup = ConnectMarkup &
  MarkUpCloudType & {
    type: 'cloud';
  };

type ConnectTextMarkup = ConnectMarkup &
  MarkUpTextType & {
    type: 'text';
  };

type ConnectLineMarkup = ConnectMarkup &
  MarkUpLineType & {
    type: 'line';
  };

type ConnectArrowMarkup = ConnectMarkup &
  MarkUpArrowType & {
    type: 'arrow';
  };

type ConnectRedlinesMarkup = ConnectMarkup &
  MarkUpRedLineType & {
    type: 'redlines';
  };

export type ProcessedConnectMarkup =
  | ConnectCloudMarkup
  | ConnectTextMarkup
  | ConnectLineMarkup
  | ConnectArrowMarkup
  | ConnectRedlinesMarkup;

export function processConnectMarkup(
  markup: ConnectMarkup
): ProcessedConnectMarkup | null {
  switch (markup.type) {
    case 'cloud':
      return markup as ConnectCloudMarkup;
    case 'text':
      return markup as ConnectTextMarkup;
    case 'line':
      return markup as ConnectLineMarkup;
    case 'arrow':
      return markup as ConnectArrowMarkup;
    case 'redlines':
      return markup as ConnectRedlinesMarkup;
    default:
      console.warn(`Markup type '${markup.type}' is not supported`);
      return null;
  }
}

export function transformConnectMarkupToMarkup(
  connectMarkup: ProcessedConnectMarkup
): Markup | null {
  if (!connectMarkup.markupId) {
    return null;
  }

  const id = connectMarkup.markupId;
  const baseMarkup = {
    id,
    viewerMarkups: [],
    color: connectMarkup.color
      ? {
          r: connectMarkup.color.r,
          g: connectMarkup.color.g,
          b: connectMarkup.color.b,
          a: connectMarkup.color.a ?? RGB_MAX_VALUE,
        }
      : { ...DEFAULT_MARKUP_COLOR, a: RGB_MAX_VALUE },
  };

  switch (connectMarkup.type) {
    case 'cloud': {
      const convertedNormal = convertConnectDirectionToViewerDirection(
        connectMarkup.planeA,
        connectMarkup.planeB,
        connectMarkup.planeC
      );
      return {
        ...baseMarkup,
        type: 'cloud',
        positionX: convertMillimetersToMeters(connectMarkup.positionX),
        positionY: convertMillimetersToMeters(connectMarkup.positionY),
        positionZ: convertMillimetersToMeters(connectMarkup.positionZ),
        planeA: convertedNormal.x,
        planeB: convertedNormal.y,
        planeC: convertedNormal.z,
        planeD: connectMarkup.planeD,
        width: convertMillimeterDiameterToMeterRadius(connectMarkup.width),
        height: convertMillimeterDiameterToMeterRadius(connectMarkup.height),
      } as CloudMarkup;
    }

    case 'text': {
      return {
        ...baseMarkup,
        type: 'text',
        positionX: convertMillimetersToMeters(connectMarkup.positionX),
        positionY: convertMillimetersToMeters(connectMarkup.positionY),
        positionZ: convertMillimetersToMeters(connectMarkup.positionZ),
        position2X: convertMillimetersToMeters(connectMarkup.position2X),
        position2Y: convertMillimetersToMeters(connectMarkup.position2Y),
        position2Z: convertMillimetersToMeters(connectMarkup.position2Z),
        text: connectMarkup.text,
        screenSpaceDistance: connectMarkup.screenSpaceDistance,
        width: connectMarkup.width,
      } as TextMarkup;
    }

    case 'line': {
      return {
        ...baseMarkup,
        type: 'line',
        positionX: convertMillimetersToMeters(connectMarkup.positionX),
        positionY: convertMillimetersToMeters(connectMarkup.positionY),
        positionZ: convertMillimetersToMeters(connectMarkup.positionZ),
        position2X: convertMillimetersToMeters(connectMarkup.position2X),
        position2Y: convertMillimetersToMeters(connectMarkup.position2Y),
        position2Z: convertMillimetersToMeters(connectMarkup.position2Z),
      } as LineMarkup;
    }

    case 'arrow': {
      return {
        ...baseMarkup,
        type: 'arrow',
        positionX: convertMillimetersToMeters(connectMarkup.positionX),
        positionY: convertMillimetersToMeters(connectMarkup.positionY),
        positionZ: convertMillimetersToMeters(connectMarkup.positionZ),
        position2X: convertMillimetersToMeters(connectMarkup.position2X),
        position2Y: convertMillimetersToMeters(connectMarkup.position2Y),
        position2Z: convertMillimetersToMeters(connectMarkup.position2Z),
      } as ArrowMarkup;
    }

    case 'redlines': {
      if (!connectMarkup.lines || connectMarkup.lines.length === 0) {
        return null;
      }

      return {
        ...baseMarkup,
        type: 'redlines',
        lines: connectMarkup.lines.map((line) => ({
          positionX: convertMillimetersToMeters(line.positionX),
          positionY: convertMillimetersToMeters(line.positionY),
          positionZ: convertMillimetersToMeters(line.positionZ),
          position2X: convertMillimetersToMeters(line.position2X),
          position2Y: convertMillimetersToMeters(line.position2Y),
          position2Z: convertMillimetersToMeters(line.position2Z),
        })),
        camera: connectMarkup.camera,
      } as RedlinesMarkup;
    }

    default:
      return null;
  }
}

/**
 * Convert a custom Web3D markup to Connect Markup format
 */
export function convertMarkupToConnectMarkup(
  customMarkup: Markup
): ProcessedConnectMarkup | null {
  switch (customMarkup.type) {
    case 'cloud': {
      // Direction conversion is an involution (negation), so this maps viewer -> Connect.
      const connectNormal = convertConnectDirectionToViewerDirection(
        customMarkup.planeA,
        customMarkup.planeB,
        customMarkup.planeC
      );
      return {
        type: 'cloud',
        positionX: convertMetersToMillimeters(customMarkup.positionX),
        positionY: convertMetersToMillimeters(customMarkup.positionY),
        positionZ: convertMetersToMillimeters(customMarkup.positionZ),
        planeA: connectNormal.x,
        planeB: connectNormal.y,
        planeC: connectNormal.z,
        planeD: customMarkup.planeD,
        width: convertMeterRadiusToMillimeterDiameter(customMarkup.width),
        height: convertMeterRadiusToMillimeterDiameter(customMarkup.height),
        color: ensureAlpha(customMarkup.color),
      } as ConnectCloudMarkup;
    }

    case 'line':
      return {
        type: 'line',
        positionX: convertMetersToMillimeters(customMarkup.positionX),
        positionY: convertMetersToMillimeters(customMarkup.positionY),
        positionZ: convertMetersToMillimeters(customMarkup.positionZ),
        position2X: convertMetersToMillimeters(customMarkup.position2X),
        position2Y: convertMetersToMillimeters(customMarkup.position2Y),
        position2Z: convertMetersToMillimeters(customMarkup.position2Z),
        color: ensureAlpha(customMarkup.color),
      } as ConnectLineMarkup;

    case 'arrow':
      return {
        type: 'arrow',
        positionX: convertMetersToMillimeters(customMarkup.positionX),
        positionY: convertMetersToMillimeters(customMarkup.positionY),
        positionZ: convertMetersToMillimeters(customMarkup.positionZ),
        position2X: convertMetersToMillimeters(customMarkup.position2X),
        position2Y: convertMetersToMillimeters(customMarkup.position2Y),
        position2Z: convertMetersToMillimeters(customMarkup.position2Z),
        color: ensureAlpha(customMarkup.color),
      } as ConnectArrowMarkup;

    case 'text':
      return {
        type: 'text',
        positionX: convertMetersToMillimeters(customMarkup.positionX),
        positionY: convertMetersToMillimeters(customMarkup.positionY),
        positionZ: convertMetersToMillimeters(customMarkup.positionZ),
        position2X: convertMetersToMillimeters(customMarkup.position2X),
        position2Y: convertMetersToMillimeters(customMarkup.position2Y),
        position2Z: convertMetersToMillimeters(customMarkup.position2Z),
        text: customMarkup.text,
        color: ensureAlpha(customMarkup.color),
      } as ConnectTextMarkup;

    case 'redlines':
      return {
        type: 'redlines',
        lines: customMarkup.lines.map((line) => ({
          positionX: convertMetersToMillimeters(line.positionX),
          positionY: convertMetersToMillimeters(line.positionY),
          positionZ: convertMetersToMillimeters(line.positionZ),
          position2X: convertMetersToMillimeters(line.position2X),
          position2Y: convertMetersToMillimeters(line.position2Y),
          position2Z: convertMetersToMillimeters(line.position2Z),
        })),
        camera: customMarkup.camera
          ? {
              targetX: customMarkup.camera.targetX,
              targetY: customMarkup.camera.targetY,
              targetZ: customMarkup.camera.targetZ,
              distance: customMarkup.camera.distance,
              pitch: customMarkup.camera.pitch,
              yaw: customMarkup.camera.yaw,
              viewAngle: customMarkup.camera.viewAngle,
              viewScale: customMarkup.camera.viewScale,
              projectionType: customMarkup.camera.projectionType,
            }
          : undefined,
        color: ensureAlpha(customMarkup.color),
      } as ConnectRedlinesMarkup;

    default:
      throw new UnreachableCaseError(customMarkup);
  }
}

let nextLiveMarkupId = 0;

/**
 * Convert a web3d ViewerMarkup (plugin instance) to the canonical Markup model.
 * Positions are stored in viewer coordinates (meters).
 */
export function convertViewerMarkupToMarkup(
  viewerMarkup: ViewerMarkup
): Markup | null {
  const color = viewerMarkup.color
    ? web3dColorToRgb(viewerMarkup.color)
    : undefined;

  const baseMarkup = {
    id: `live-markup-${++nextLiveMarkupId}`,
    viewerMarkups: [viewerMarkup],
    color,
  };

  if (viewerMarkup instanceof Web3DCloudMarkup) {
    const n = viewerMarkup.normal;
    const planeA = n?.x ?? 0;
    const planeB = n?.y ?? 0;
    const planeC = n?.z ?? 1;
    const len = Math.hypot(planeA, planeB, planeC);
    const eps = 1e-10;
    return {
      ...baseMarkup,
      type: 'cloud',
      positionX: viewerMarkup.position.x,
      positionY: viewerMarkup.position.y,
      positionZ: viewerMarkup.position.z,
      planeA: len > eps ? planeA / len : 0,
      planeB: len > eps ? planeB / len : 0,
      planeC: len > eps ? planeC / len : 1,
      planeD: 0,
      width: viewerMarkup.width,
      height: viewerMarkup.height,
    };
  }

  if (viewerMarkup instanceof Web3DTextMarkup) {
    // Canonical text convention (matches Connect): position = label, position2 = anchor.
    // In the viewer, endPick.point drives the text label position and startPick.point
    // is the anchor on the geometry, so the mapping is swapped relative to startPick/endPick.
    return {
      ...baseMarkup,
      type: 'text',
      positionX: viewerMarkup.endPick?.point?.x ?? 0,
      positionY: viewerMarkup.endPick?.point?.y ?? 0,
      positionZ: viewerMarkup.endPick?.point?.z ?? 0,
      position2X: viewerMarkup.startPick?.point?.x ?? 0,
      position2Y: viewerMarkup.startPick?.point?.y ?? 0,
      position2Z: viewerMarkup.startPick?.point?.z ?? 0,
      text: viewerMarkup.text,
    };
  }

  if (viewerMarkup instanceof Web3DArrowMarkup) {
    return {
      ...baseMarkup,
      type: 'arrow',
      positionX: viewerMarkup.startPick?.point?.x ?? 0,
      positionY: viewerMarkup.startPick?.point?.y ?? 0,
      positionZ: viewerMarkup.startPick?.point?.z ?? 0,
      position2X: viewerMarkup.endPick?.point?.x ?? 0,
      position2Y: viewerMarkup.endPick?.point?.y ?? 0,
      position2Z: viewerMarkup.endPick?.point?.z ?? 0,
    };
  }

  if (viewerMarkup instanceof Web3DLineMarkup) {
    return {
      ...baseMarkup,
      type: 'line',
      positionX: viewerMarkup.startPick?.point?.x ?? 0,
      positionY: viewerMarkup.startPick?.point?.y ?? 0,
      positionZ: viewerMarkup.startPick?.point?.z ?? 0,
      position2X: viewerMarkup.endPick?.point?.x ?? 0,
      position2Y: viewerMarkup.endPick?.point?.y ?? 0,
      position2Z: viewerMarkup.endPick?.point?.z ?? 0,
    };
  }

  if (viewerMarkup instanceof Web3DFreelineMarkup) {
    // Freeline points are stored in local space (relative to markup.position).
    // Convert to world space so replication / Connect persistence place the
    // polyline at the correct location on other viewers.
    viewerMarkup.updateMatrixWorld(true);
    const lines: RedlinesMarkup['lines'] = [];
    const points = viewerMarkup.points;
    for (let i = 0; i < points.length - 1; i++) {
      const start = points[i].clone().applyMatrix4(viewerMarkup.matrixWorld);
      const end = points[i + 1].clone().applyMatrix4(viewerMarkup.matrixWorld);
      lines.push({
        positionX: start.x,
        positionY: start.y,
        positionZ: start.z,
        position2X: end.x,
        position2Y: end.y,
        position2Z: end.z,
      });
    }
    return {
      ...baseMarkup,
      type: 'redlines',
      lines,
    };
  }

  return null;
}

/**
 * Create a web3d markup on a viewer from the canonical Markup model.
 * Uses raw viewer coordinates (meters) — no mm conversion.
 */
export function addMarkupToViewer(
  markup: Markup,
  viewer: Web3DViewer,
  origin?: unknown
): ViewerMarkup | null {
  const color = markup.color
    ? rgbToWeb3dColor(markup.color)
    : rgbToWeb3dColor(DEFAULT_MARKUP_COLOR);

  let viewerMarkup: ViewerMarkup | null = null;

  switch (markup.type) {
    case 'cloud': {
      const tool = viewer.tools.get(CloudMarkupTool);
      if (!tool) return null;
      viewerMarkup = tool.add(
        {
          point: new Vector3(
            markup.positionX,
            markup.positionY,
            markup.positionZ
          ),
          normal: new Vector3(markup.planeA, markup.planeB, markup.planeC),
        },
        markup.width,
        markup.height,
        color,
        origin
      );
      break;
    }
    case 'line': {
      const tool = viewer.tools.get(LineMarkupTool);
      if (!tool) return null;
      viewerMarkup = tool.add(
        {
          point: new Vector3(
            markup.positionX,
            markup.positionY,
            markup.positionZ
          ),
        },
        {
          point: new Vector3(
            markup.position2X,
            markup.position2Y,
            markup.position2Z
          ),
        },
        color,
        origin
      );
      break;
    }
    case 'arrow': {
      const tool = viewer.tools.get(ArrowMarkupTool);
      if (!tool) return null;
      viewerMarkup = tool.add(
        {
          point: new Vector3(
            markup.positionX,
            markup.positionY,
            markup.positionZ
          ),
        },
        {
          point: new Vector3(
            markup.position2X,
            markup.position2Y,
            markup.position2Z
          ),
        },
        color,
        origin
      );
      break;
    }
    case 'text': {
      const tool = viewer.tools.get(TextMarkupTool);
      if (!tool) return null;
      viewerMarkup = tool.add(
        {
          point: new Vector3(
            markup.position2X,
            markup.position2Y,
            markup.position2Z
          ),
        },
        {
          point: new Vector3(
            markup.positionX,
            markup.positionY,
            markup.positionZ
          ),
        },
        color,
        markup.text,
        origin
      );
      break;
    }
    case 'redlines': {
      const worldPoints: Vector3[] = [];
      for (const line of markup.lines) {
        appendRedlineSegmentToPolyline(
          worldPoints,
          new Vector3(line.positionX, line.positionY, line.positionZ),
          new Vector3(line.position2X, line.position2Y, line.position2Z)
        );
      }
      viewerMarkup = createFreelineMarkupFromWorldPoints(
        viewer,
        worldPoints,
        color,
        origin
      );
      break;
    }
    default:
      throw new UnreachableCaseError(markup);
  }

  if (viewerMarkup) {
    stampCanonicalId(viewerMarkup, markup.id);
  }

  return viewerMarkup;
}
